import React, { useEffect, useRef } from 'react';

export interface TextAreaProps {
  label: string;
  value: string;
  onChange?: (v: string) => void;
  error?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  icon?: React.ReactNode;
  className?: string;

  rows?: number;
  minRows?: number;
  maxLength?: number;
  showCount?: boolean;
  autoResize?: boolean;
  maxAutoHeight?: number;
  name?: string;
}

export const TextArea: React.FC<TextAreaProps> = ({
  label,
  value,
  onChange,
  error,
  placeholder,
  required = false,
  disabled = false,
  icon,
  className,
  rows,
  minRows = 3,
  maxLength,
  showCount = false,
  autoResize = true,
  maxAutoHeight = 280,
  name,
}) => {
  const taRef = useRef<HTMLTextAreaElement | null>(null);

  const inputClass = [
    'mt-1 w-full rounded-md border px-3 py-2 shadow-sm',
    'focus:outline-none focus:ring-2',
    error
      ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500'
      : 'border-gray-300 focus:ring-blue-500',
    icon ? 'pl-10' : '',
    disabled ? 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-600' : '',
    'resize-none overflow-y-auto',
  ]
    .join(' ')
    .trim();

  const baseId = label.replace(/\s+/g, '-').toLowerCase();
  const inputId = `${baseId}-textarea`;
  const errorId = error ? `${baseId}-error` : undefined;

  useEffect(() => {
    if (!autoResize || !taRef.current) return;
    const el = taRef.current;

    const cs = window.getComputedStyle(el);
    const lineHeight = parseFloat(cs.lineHeight) || 20;
    const paddingTop = parseFloat(cs.paddingTop) || 0;
    const paddingBottom = parseFloat(cs.paddingBottom) || 0;
    const borderTop = parseFloat(cs.borderTopWidth) || 0;
    const borderBottom = parseFloat(cs.borderBottomWidth) || 0;
    const minHeight = lineHeight * minRows + paddingTop + paddingBottom + borderTop + borderBottom;

    el.style.height = 'auto';
    const scrollH = el.scrollHeight;
    const target = Math.max(minHeight, Math.min(scrollH, maxAutoHeight));
    el.style.height = `${target}px`;
    el.style.overflowY = scrollH > maxAutoHeight ? 'auto' : 'hidden';
  }, [value, autoResize, minRows, maxAutoHeight]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange?.(e.target.value);
  };

  return (
    <div className={className}>
      <label htmlFor={inputId} className="block text-sm font-medium text-gray-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>

      <div className="relative">
        {icon && (
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-start pt-2 pl-3">
            <span className="text-gray-500 sm:text-sm">{icon}</span>
          </div>
        )}

        <textarea
          ref={taRef}
          id={inputId}
          name={name}
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          disabled={disabled}
          className={inputClass}
          rows={rows ?? minRows}
          maxLength={maxLength}
          aria-invalid={!!error}
          aria-describedby={errorId}
          style={{ maxHeight: maxAutoHeight }}
        />

        {error && (
          <div id={errorId} className="mt-1 text-xs text-red-600">
            {error}
          </div>
        )}
      </div>

      {(showCount || typeof maxLength === 'number') && (
        <div className="mt-1 text-xs text-gray-500 text-right">
          {value.length}
          {typeof maxLength === 'number' ? ` / ${maxLength}` : null}
        </div>
      )}
    </div>
  );
};